import { Camera, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import {
  PermissionState,
  MediaPermissionsStatus,
  getPermissionResetInstructions,
  getFirefoxAndroidWarning,
} from '@common/media';
import { WarningMessage } from '@common/components';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './dialog';
import { Button } from '../shadcn/button';
import MicrophoneIcon from '../icons/MicrophoneIcon';
import { translations } from '../../translations/chatInputTranslations';
import type { ICreateThemeData } from '../../types';

interface PermissionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onGrantAccess: () => Promise<void>;
  requestedPermissions: { video?: boolean; audio?: boolean };
  permissions: MediaPermissionsStatus | null;
  themeData?: ICreateThemeData;
  needsPermissionDialog?: boolean;
  videoEnabled?: boolean;
  audioEnabled?: boolean;
  isFirefoxAndroid?: boolean;
  languageCode?: string;
  isGrantingAccess?: boolean;
  isTextRightToLeft: boolean;
}

export function PermissionDialog({
  isOpen,
  onClose,
  onGrantAccess,
  requestedPermissions,
  permissions,
  themeData,
  needsPermissionDialog,
  videoEnabled,
  audioEnabled,
  isFirefoxAndroid,
  languageCode = 'en',
  isGrantingAccess = false,
  isTextRightToLeft = false,
}: PermissionDialogProps) {
  // Get translations for current language
  const t = translations[languageCode] || translations.en;
  // Determine what to show based on auto-dialog vs manual request
  const showVideo = requestedPermissions.video || (needsPermissionDialog && videoEnabled);
  const showAudio = requestedPermissions.audio || (needsPermissionDialog && audioEnabled);

  // Check if any permissions are denied (for better button text)
  const hasVideoDenied = !!(showVideo && permissions?.camera === PermissionState.DENIED);
  const hasAudioDenied = !!(showAudio && permissions?.microphone === PermissionState.DENIED);
  const hasAnyDenied = !!(hasVideoDenied || hasAudioDenied);

  // Check if all requested permissions are already granted
  const videoGranted = !showVideo || permissions?.camera === PermissionState.GRANTED;
  const audioGranted = !showAudio || permissions?.microphone === PermissionState.GRANTED;
  const allGranted = videoGranted && audioGranted;

  if (!showVideo && !showAudio) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-md gap-6"
        onPointerDownOutside={(e) => e.preventDefault()}
        dir="auto"
      >
        <DialogHeader className={`${isTextRightToLeft ? 'md:text-right mt-4' : 'text-left'}`}>
          <DialogTitle className="mb-2">{t.mediaAccess}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3" dir="auto">
          {/*
            Warning component for reset instructions for blocked permissions or Firefox Android browser warning
          */}
          {(hasAnyDenied || isFirefoxAndroid) && (
            <WarningMessage>
              <p className="text-xs leading-relaxed">
                {hasAnyDenied
                  ? getPermissionResetInstructions(hasVideoDenied, hasAudioDenied, languageCode)
                  : getFirefoxAndroidWarning(languageCode)}
              </p>
            </WarningMessage>
          )}

          {/* Permission Status */}
          <div className="space-y-3">
            {showVideo && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <Camera className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="font-medium">{t.camera}</span>
                </div>
                <div className="flex items-center gap-2 min-w-[120px] justify-end">
                  {permissions?.camera === PermissionState.GRANTED ? (
                    <div className="flex items-center gap-2 min-w-[120px] justify-end">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-green-700 text-right leading-none flex items-center">
                        {t.granted}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 min-w-[120px] justify-end">
                      <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-yellow-700 text-right leading-none flex items-center">
                        {t.required}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showAudio && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <MicrophoneIcon className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="font-medium">{t.microphone}</span>
                </div>
                <div className="flex items-center gap-2 min-w-[120px] justify-end">
                  {permissions?.microphone === PermissionState.GRANTED ? (
                    <div className="flex items-center gap-2 min-w-[120px] justify-end">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-green-700 text-right leading-none flex items-center">
                        {t.granted}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 min-w-[120px] justify-end">
                      <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-yellow-700 text-right leading-none flex items-center">
                        {t.required}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter
          className={`flex-col sm:flex-row gap-2 ${isTextRightToLeft ? 'md:justify-start' : ''}`}
        >
          {allGranted ? (
            <Button
              onClick={onClose}
              className="w-full"
              style={{ backgroundColor: themeData?.primary || '#096846' }}
            >
              {t.continueButton}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
                {t.skipForNow}
              </Button>
              <Button
                onClick={onGrantAccess}
                className="w-full sm:w-auto"
                style={{ backgroundColor: themeData?.primary || '#096846' }}
                disabled={isFirefoxAndroid || isGrantingAccess}
              >
                {isGrantingAccess && (
                  <Loader2
                    className={`${isTextRightToLeft ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`}
                  />
                )}
                {hasAnyDenied ? t.tryAgain : t.grantAccess}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
